import type { intlFormatPropTypes } from "react-intl";
import { z } from "zod";

import {
  MAX_GRATUITY_RATE,
  MIN_GRATUITY_RATE,
  MIN_SPACE_RENTAL,
  ROOM_TYPES,
} from "./ListingFsvForm.consts";

type BuildListingFsvFormSchemaArgs = {
  intl: intlFormatPropTypes;
};

// A cleared number input is "", which z.coerce.number() would turn into 0;
// map it to undefined so it fails as missing instead
const toNumberOrUndefined = (val: unknown) =>
  val === "" || val == null ? undefined : Number(val);

// preprocess widens the input type to unknown; the form holds numbers, so
// keep the resolver typed as number in and out (same as ListingRulesForm)
const requiredNumber = (schema: z.ZodNumber) =>
  z.preprocess(toNumberOrUndefined, schema) as unknown as z.ZodType<
    number,
    z.ZodTypeDef,
    number
  >;

const buildListingFsvFormSchema = ({ intl }: BuildListingFsvFormSchemaArgs) => {
  const foodAndDrinkMinimumRequired = intl.formatMessage({
    id: "hosting.listing_fsv.food_and_drink_minimum_required",
    defaultMessage: "Please enter a food and drink minimum",
  });
  return (
    z
      .object({
        foodAndDrinkMinimum: requiredNumber(
          z
            .number({
              invalid_type_error: foodAndDrinkMinimumRequired,
              required_error: foodAndDrinkMinimumRequired,
            })
            .min(0),
        ),
        hasSpaceRental: z.boolean(),
        spaceRental: z.coerce.number().min(0),
        roomType: z.enum(ROOM_TYPES),
        menuUrl: z.string().optional(),
        hasGratuity: z.boolean(),
        gratuityRate: z.coerce.number(),
      })
      // The amount only counts while the toggle is on; off is sent as 0
      .refine(
        ({ hasSpaceRental, spaceRental }) =>
          !hasSpaceRental || spaceRental >= MIN_SPACE_RENTAL,
        {
          path: ["spaceRental"],
          message: intl.formatMessage(
            {
              id: "hosting.listing_fsv.space_rental_min_error",
              defaultMessage: "Space rental must be at least {amount}.",
            },
            { amount: MIN_SPACE_RENTAL },
          ),
        },
      )
      // Same for gratuity: the rate only counts while the toggle is on
      .refine(
        ({ hasGratuity, gratuityRate }) =>
          !hasGratuity ||
          (gratuityRate >= MIN_GRATUITY_RATE &&
            gratuityRate <= MAX_GRATUITY_RATE),
        {
          path: ["gratuityRate"],
          message: intl.formatMessage(
            {
              id: "hosting.listing_fsv.gratuity_range_error",
              defaultMessage: "Gratuity rate must be between {min}% and {max}%",
            },
            { min: MIN_GRATUITY_RATE, max: MAX_GRATUITY_RATE },
          ),
        },
      )
  );
};

type ListingFsvFormValues = z.infer<
  ReturnType<typeof buildListingFsvFormSchema>
>;

export { buildListingFsvFormSchema };
export type { ListingFsvFormValues };
